/* ══════════════════════════════════════════════════
   ARAMÉ — Core Theme JS
   Initialises Lenis, GSAP, ScrollTrigger, and header scroll behaviour.
   ══════════════════════════════════════════════════ */

(function () {
  'use strict';

  /* Wait for GSAP + Lenis to be available (loaded via CDN defer) */
  function ready(fn) {
    if (document.readyState !== 'loading') {
      fn();
    } else {
      document.addEventListener('DOMContentLoaded', fn);
    }
  }

  function waitForLibs(fn) {
    const check = () => {
      if (window.gsap && window.ScrollTrigger && window.Lenis) {
        fn();
      } else {
        setTimeout(check, 50);
      }
    };
    check();
  }

  ready(function () {
    initResilientLinks();
    initPartnershipForm();
    initHeroSlideshow();
    document.addEventListener('shopify:section:load', (event) => {
      initHeroSlideshow(event.target);
    });
    waitForLibs(initTheme);
  });

  function initTheme() {
    gsap.registerPlugin(ScrollTrigger);
    ScrollTrigger.config({ ignoreMobileResize: true });

    /* ── Lenis smooth scroll ── */
    const lenis = new Lenis({
      duration: 1.08,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      smoothWheel: true,
      syncTouch: true,
    });

    window.lenis = lenis;
    lenis.on('scroll', ScrollTrigger.update);

    // iOS/Android: native touch scroll doesn't always fire Lenis events during momentum —
    // listen to the raw window scroll so ScrollTrigger always stays in sync.
    window.addEventListener('scroll', () => ScrollTrigger.update(), { passive: true });

    const ticker = (time) => lenis.raf(time * 1000);
    gsap.ticker.add(ticker);
    gsap.ticker.lagSmoothing(0);

    // Refresh ScrollTrigger once fonts + images settle so trigger positions are correct.
    const refreshST = () => setTimeout(() => ScrollTrigger.refresh(), 400);
    if (document.readyState === 'complete') { refreshST(); }
    else { window.addEventListener('load', refreshST, { once: true }); }

    /* ── Header scroll behaviour ── */
    initHeader();

    /* ── Scroll progress bar ── */
    initScrollProgress();

    /* ── Floating CTA ── */
    initFloatingCta();

    /* ── Mobile nav ── */
    initMobileNav();

    /* ── Page/product scroll buttons ── */
    initGenericScrollButtons();

    /* ── Section animations (non-canvas) ── */
    initSectionAnimations();

    /* ── Studio Partnerships page ── */
    initPartnershipPage();

    /* ── Mobile safety net (fires after GSAP has had time to register) ── */
    initAnimSafetyNet();

    /* ── Hero polish ── */
    initHero();

    /* ── Scroll Reveal canvas ── */
    const mainReveal = document.querySelector('.scroll-reveal-section');
    if (mainReveal) {
      loadScript(mainReveal.dataset.jsUrl, function () {
        if (window.initScrollReveal) window.initScrollReveal();
      });
    }

    /* ── Inside Set Reveal canvas ── */
    if (document.getElementById('insideSetSection')) {
      loadScript(document.getElementById('insideSetSection').dataset.jsUrl, function () {
        if (window.initInsideSetReveal) window.initInsideSetReveal();
      });
    }
  }

  /* ── Helpers ── */
  function loadScript(src, cb) {
    if (!src) return;
    const s = document.createElement('script');
    s.src = src;
    s.onload = cb;
    document.head.appendChild(s);
  }

  function previewSearchParams() {
    const params = new URLSearchParams(window.location.search);
    const keep = new URLSearchParams();
    ['preview_theme_id', '_fd', 'pb', 'pb_preview', 'surface_detail', 'surface_inter_position', 'surface_intra_position', 'surface_type'].forEach((key) => {
      if (params.has(key)) keep.set(key, params.get(key));
    });
    return keep;
  }

  function withPreviewParams(url) {
    const keep = previewSearchParams();
    if (!keep.toString()) return url;

    try {
      const next = new URL(url, window.location.origin);
      if (next.origin !== window.location.origin) return url;
      keep.forEach((value, key) => {
        if (!next.searchParams.has(key)) next.searchParams.set(key, value);
      });
      return next.pathname + next.search + next.hash;
    } catch (err) {
      return url;
    }
  }

  function closeMobileNavIfOpen() {
    const nav = document.getElementById('mobileNav');
    const menuBtn = document.getElementById('headerMenuBtn');
    if (!nav || !nav.classList.contains('open')) return;
    nav.classList.remove('open');
    nav.setAttribute('aria-hidden', 'true');
    if (menuBtn) {
      menuBtn.setAttribute('aria-expanded', 'false');
      menuBtn.classList.remove('open');
    }
    document.body.style.overflow = '';
    if (window.lenis) window.lenis.start();
  }

  function navigateToHash(hash) {
    const target = document.querySelector(hash);
    if (target) {
      scrollTo(target);
      return;
    }

    const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
    window.location.href = withPreviewParams(root + hash);
  }

  function initResilientLinks() {
    document.addEventListener('click', function (event) {
      const scrollBtn = event.target.closest('[data-scroll-to]');
      if (scrollBtn) {
        const selector = scrollBtn.getAttribute('data-scroll-to');
        if (!selector) return;
        event.preventDefault();
        event.stopImmediatePropagation();
        closeMobileNavIfOpen();
        if (selector.charAt(0) === '#') {
          navigateToHash(selector);
          return;
        }
        const target = document.querySelector(selector);
        if (target) scrollTo(target);
        return;
      }

      const link = event.target.closest('a[href]');
      if (!link) return;
      const href = link.getAttribute('href');
      if (!href || href === '#') return;
      if (href.indexOf('mailto:') === 0 || href.indexOf('tel:') === 0) return;
      if (link.target && link.target !== '_self') return;

      let next;
      try {
        next = new URL(href, window.location.origin);
      } catch (err) {
        return;
      }

      if (next.origin !== window.location.origin) return;

      const samePath = next.pathname === window.location.pathname;
      if (next.hash && samePath) {
        const target = document.querySelector(next.hash);
        if (target) {
          event.preventDefault();
          closeMobileNavIfOpen();
          scrollTo(target);
        }
        return;
      }

      const preserved = withPreviewParams(next.pathname + next.search + next.hash);
      if (preserved !== next.pathname + next.search + next.hash) {
        event.preventDefault();
        closeMobileNavIfOpen();
        window.location.href = preserved;
      }
    }, true);
  }

  function scrollTo(target) {
    if (!target) return;
    if (typeof target === 'string') {
      target = document.querySelector(target);
      if (!target) return;
    }
    if (target.classList && target.classList.contains('scroll-reveal-section')) {
      scrollToRevealStart(target);
      return;
    }
    if (window.lenis) {
      window.lenis.scrollTo(target, {
        duration: 0.9,
        easing: (t) => 1 - Math.pow(1 - t, 3),
      });
    } else {
      target.scrollIntoView({ behavior: 'smooth' });
    }
  }

  function forceMainRevealFrame() {
    if (window.arameMainReveal && typeof window.arameMainReveal.drawFirstFrame === 'function') {
      window.arameMainReveal.drawFirstFrame();
    }
  }

  function scrollToRevealStart(target) {
    if (!target) return;

    forceMainRevealFrame();

    if (window.lenis) {
      window.lenis.scrollTo(target, {
        duration: 0.9,
        easing: (t) => 1 - Math.pow(1 - t, 3),
        onComplete: forceMainRevealFrame,
      });
    } else {
      target.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setTimeout(forceMainRevealFrame, 900);
    }
  }

  /* ── Header ── */
  function initHeader() {
    const header = document.querySelector('.site-header');
    if (!header) return;

    // On mission page the header is always frosted (handled by always-frosted class in Liquid)
    if (header.classList.contains('always-frosted')) return;

    function onScroll() {
      header.classList.toggle('scrolled', window.scrollY > 60);
    }
    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();

    // Nav anchor links
    header.querySelectorAll('[data-scroll-to]').forEach((btn) => {
      btn.addEventListener('click', () => {
        const target = document.querySelector(btn.dataset.scrollTo);
        if (target) {
          scrollTo(target);
        } else {
          // On non-home pages, navigate to home with hash
          window.location.href = '/' + btn.dataset.scrollTo;
        }
      });
    });

    // Cart button
    const cartBtn = header.querySelector('.header-cart-btn');
    if (cartBtn) {
      cartBtn.addEventListener('click', () => {
        window.dispatchEvent(new CustomEvent('arame:cart:open'));
      });
    }
  }

  /* ── Hero ── */
  function initHero() {
    initHeroSlideshow();
  }

  function initHeroSlideshow(root) {
    const scope = root || document;
    const sliders = Array.from(scope.querySelectorAll('[data-arame-hero-slideshow]'));
    if (!sliders.length) return;

    sliders.forEach((slider) => {
      if (slider.dataset.heroReady === 'true') return;
      slider.dataset.heroReady = 'true';

      const slides = Array.from(slider.querySelectorAll('.arame-hero-slide'));
      const dots = Array.from(slider.querySelectorAll('[data-hero-dot]'));
      const prev = slider.querySelector('[data-hero-prev]');
      const next = slider.querySelector('[data-hero-next]');
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)');
      const interval = Math.max(parseInt(slider.dataset.autoplayInterval, 10) || 7500, 4000);
      let current = Math.max(slides.findIndex((slide) => slide.classList.contains('is-active')), 0);
      let timer = null;
      let paused = false;

      if (slides.length < 2) return;

      const setSlide = (index) => {
        current = (index + slides.length) % slides.length;

        slides.forEach((slide, slideIndex) => {
          const active = slideIndex === current;
          slide.classList.toggle('is-active', active);
          slide.setAttribute('aria-hidden', active ? 'false' : 'true');
        });

        dots.forEach((dot, dotIndex) => {
          const active = dotIndex === current;
          dot.classList.toggle('is-active', active);
          dot.setAttribute('aria-current', active ? 'true' : 'false');
        });
      };

      const stop = () => {
        if (timer) {
          window.clearInterval(timer);
          timer = null;
        }
      };

      const start = () => {
        stop();
        if (paused || reduceMotion.matches || document.hidden) return;
        timer = window.setInterval(() => setSlide(current + 1), interval);
      };

      const restart = () => {
        stop();
        start();
      };

      const pause = () => {
        paused = true;
        stop();
      };

      const resume = () => {
        paused = false;
        start();
      };

      if (prev) {
        prev.addEventListener('click', () => {
          setSlide(current - 1);
          restart();
        });
      }

      if (next) {
        next.addEventListener('click', () => {
          setSlide(current + 1);
          restart();
        });
      }

      dots.forEach((dot) => {
        dot.addEventListener('click', () => {
          setSlide(parseInt(dot.dataset.heroDot, 10) || 0);
          restart();
        });
      });

      slider.addEventListener('focusin', pause);
      slider.addEventListener('focusout', (event) => {
        if (!slider.contains(event.relatedTarget)) resume();
      });

      document.addEventListener('visibilitychange', () => {
        if (document.hidden) stop();
        else start();
      });

      if (reduceMotion.addEventListener) {
        reduceMotion.addEventListener('change', start);
      }

      setSlide(current);
      start();
    });
  }

  /* ── Section animations using Intersection Observer + GSAP ── */
  function initSectionAnimations() {
    /* Brand Story */
    animateOnScroll('.brand-story-section', function (el) {
      const line = el.querySelector('.brand-story-line');
      const label = el.querySelector('.brand-story-label');
      const heading = el.querySelector('.brand-story-heading');
      const body = el.querySelector('.brand-story-body');
      const img = el.querySelector('.brand-story-image-inner img');

      if (line) gsap.fromTo(line, { scaleX: 0 }, { scaleX: 1, duration: 1.2, ease: 'power3.inOut' });
      if (label) gsap.fromTo(label, { opacity: 0, y: 12 }, { opacity: 1, y: 0, duration: 0.8, delay: 0.1, ease: 'power3.out' });
      if (heading) gsap.fromTo(heading, { clipPath: 'inset(100% 0 0 0)', y: 16 }, { clipPath: 'inset(0% 0 0 0)', y: 0, duration: 1.2, delay: 0.2, ease: 'power4.out' });
      if (body) gsap.fromTo(body, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 1.0, delay: 0.4, ease: 'power3.out' });
      if (img) gsap.fromTo(img, { scale: 1.06 }, { scale: 1, duration: 1.4, delay: 0.2, ease: 'power3.out' });
    });

    /* Features — Studio Set details */
    const featuresSection = document.querySelector('.features-section');
    if (featuresSection) {
      const featTrigger = { trigger: featuresSection, start: 'top 82%', once: true };
      const featLine = featuresSection.querySelector('.features-line');
      const featEyebrow = featuresSection.querySelector('.features-eyebrow');
      const featHeading = featuresSection.querySelector('.features-heading');
      const featSub = featuresSection.querySelector('.features-subheading');
      const featItems = Array.from(featuresSection.querySelectorAll('.feature-item'));

      if (featLine) gsap.fromTo(featLine, { scaleX: 0 }, { scaleX: 1, duration: 1.2, ease: 'power3.inOut', scrollTrigger: featTrigger });
      if (featEyebrow) gsap.fromTo(featEyebrow, { opacity: 0, y: 10 }, { opacity: 1, y: 0, duration: 0.8, delay: 0.1, ease: 'power3.out', scrollTrigger: featTrigger });
      if (featHeading) gsap.fromTo(featHeading, { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 1.0, delay: 0.2, ease: 'power3.out', scrollTrigger: featTrigger });
      if (featSub) gsap.fromTo(featSub, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 1.0, delay: 0.35, ease: 'power3.out', scrollTrigger: featTrigger });
      if (featItems.length) {
        gsap.fromTo(featItems, { opacity: 0, y: 36 }, {
          opacity: 1, y: 0, duration: 0.9, stagger: 0.1, delay: 0.1, ease: 'power3.out',
          scrollTrigger: { trigger: featuresSection, start: 'top 70%', once: true },
        });
      }
    }

    /* Sock Aesthetic — inline scrollTrigger so initial opacity:0 is set immediately (no snap on entry) */
    const sockSection = document.querySelector('.sock-aesthetic-section');
    if (sockSection) {
      const sockImg = sockSection.querySelector('.sock-image-wrap img');
      const sockLine = sockSection.querySelector('.sock-accent-line');
      const sockChildren = Array.from(sockSection.querySelectorAll('.sock-label, .sock-heading, .sock-divider, .sock-body, .sock-chips'));
      const sockTrigger = { trigger: sockSection, start: 'top 82%', once: true };

      if (sockImg) gsap.fromTo(sockImg, { clipPath: 'inset(8% 0 0 0)', scale: 1.04 }, { clipPath: 'inset(0% 0 0 0)', scale: 1, duration: 1.4, ease: 'power3.out', scrollTrigger: sockTrigger });
      if (sockLine) gsap.fromTo(sockLine, { scaleX: 0 }, { scaleX: 1, duration: 1.0, delay: 0.2, ease: 'power3.inOut', scrollTrigger: sockTrigger });
      if (sockChildren.length) gsap.fromTo(sockChildren, { opacity: 0, y: 32 }, { opacity: 1, y: 0, stagger: 0.12, duration: 1.0, delay: 0.2, ease: 'power3.out', scrollTrigger: sockTrigger });
    }

    /* Material Detail — same inline pattern, once:true, no exit reset */
    const materialSection = document.querySelector('.material-section');
    if (materialSection) {
      const matHeading = materialSection.querySelector('.material-heading');
      const matSub = materialSection.querySelector('.material-subheading');
      const matImg = materialSection.querySelector('.material-image-wrap');
      const matTrigger = { trigger: materialSection, start: 'top 82%', once: true };
      if (matHeading) gsap.fromTo(matHeading, { opacity: 0, y: 28 }, { opacity: 1, y: 0, duration: 1.1, ease: 'power3.out', scrollTrigger: matTrigger });
      if (matSub) gsap.fromTo(matSub, { opacity: 0, y: 18 }, { opacity: 1, y: 0, duration: 1.0, delay: 0.15, ease: 'power3.out', scrollTrigger: matTrigger });
      if (matImg) gsap.fromTo(matImg, { opacity: 0, y: 30 }, { opacity: 1, y: 0, duration: 1.3, delay: 0.1, ease: 'power3.out', scrollTrigger: matTrigger });
    }

    document.querySelectorAll('.material-detail-row').forEach((row, i) => {
      gsap.fromTo(row, { opacity: 0, y: 24 }, { opacity: 1, y: 0, duration: 0.85, delay: i * 0.09, ease: 'power3.out', scrollTrigger: { trigger: row, start: 'top 90%', once: true } });
    });

    /* Colorways header */
    animateOnScroll('.colorways-header', function (el) {
      gsap.fromTo(el, { y: 30, opacity: 0 }, { y: 0, opacity: 1, duration: 1.0, ease: 'power3.out' });
    });

    /* Colorway cards — single batch animation, gentle fade-up only */
    const colorwaysSection = document.querySelector('.colorways-section');
    const productCards = document.querySelectorAll('.product-card');
    if (colorwaysSection && productCards.length) {
      gsap.fromTo(Array.from(productCards),
        { opacity: 0, y: 40 },
        {
          opacity: 1,
          y: 0,
          duration: 1.0,
          stagger: 0.15,
          ease: 'power3.out',
          scrollTrigger: { trigger: colorwaysSection, start: 'top 75%', once: true },
        }
      );
    }

    /* Movement Lifestyle */
    animateOnScroll('.movement-section', function (el) {
      const heading = el.querySelector('.movement-heading');
      const body = el.querySelector('.movement-body');
      const main = el.querySelector('.movement-main-panel');
      const col = el.querySelector('.movement-right-col');
      if (heading) gsap.fromTo(heading, { y: 32, opacity: 0 }, { y: 0, opacity: 1, duration: 1.1, ease: 'power3.out' });
      if (body) gsap.fromTo(body, { y: 20, opacity: 0 }, { y: 0, opacity: 1, duration: 0.9, delay: 0.25, ease: 'power3.out' });
      if (main) gsap.fromTo(main, { y: 28, opacity: 0 }, { y: 0, opacity: 1, duration: 1.0, ease: 'power3.out' });
      if (col) gsap.fromTo(col, { y: 28, opacity: 0 }, { y: 0, opacity: 1, duration: 1.0, delay: 0.12, ease: 'power3.out' });
    });

    /* Waitlist */
    animateOnScroll('.waitlist-section', function (el) {
      const children = Array.from(el.querySelector('.waitlist-inner')?.children || []);
      if (children.length) {
        gsap.fromTo(children, { y: 36, opacity: 0 }, { y: 0, opacity: 1, stagger: 0.14, duration: 1.0, ease: 'power3.out' });
      }
    });

    /* Footer */
    animateOnScroll('.site-footer', function (el) {
      const wordmark = el.querySelector('.footer-wordmark');
      if (wordmark) gsap.fromTo(wordmark.closest('[data-animate-footer]') || wordmark, { opacity: 0, y: 20 }, { opacity: 1, y: 0, duration: 1.0, ease: 'power3.out' });
    }, 0.88);
  }

  /* animateOnScroll — fires GSAP animation once when element enters viewport */
  function animateOnScroll(target, fn, threshold) {
    const el = typeof target === 'string' ? document.querySelector(target) : target;
    if (!el) return;

    ScrollTrigger.create({
      trigger: el,
      start: 'top ' + (typeof threshold === 'number' ? (threshold * 100) + '%' : '82%'),
      once: true,
      onEnter: () => {
        if (el.dataset.arameAnimated === 'true') return;
        el.dataset.arameAnimated = 'true';
        fn(el);
      },
    });
  }

  /* ── Scroll progress ── */
  function initScrollProgress() {
    const bar = document.querySelector('.scroll-progress');
    if (!bar) return;
    function update() {
      const scrollTop = window.scrollY;
      const docHeight = document.documentElement.scrollHeight - window.innerHeight;
      bar.style.transform = 'scaleX(' + (docHeight > 0 ? scrollTop / docHeight : 0) + ')';
    }
    window.addEventListener('scroll', update, { passive: true });
    update();
  }

  /* ── Floating CTA ── */
  function initFloatingCta() {
    const cta = document.getElementById('floatingCta');
    if (!cta) return;
    if (document.querySelector('.partnership-page')) {
      cta.style.display = 'none';
      return;
    }

    const closeBtn = cta.querySelector('.floating-cta-close');
    const ctaBtn = cta.querySelector('.floating-cta-btn');
    let dismissed = false;
    let shown = false;

    if (ctaBtn) {
      ctaBtn.addEventListener('click', () => {
        const target = document.querySelector('#waitlist');
        if (target) scrollTo(target);
        else window.location.href = '/#waitlist';
      });
    }

    if (closeBtn) {
      closeBtn.addEventListener('click', () => {
        dismissed = true;
        cta.classList.remove('active');
      });
    }

    function onScroll() {
      if (dismissed) return;
      const hero = document.getElementById('hero');
      const waitlist = document.getElementById('waitlist');
      const pastHero = hero ? window.scrollY > hero.offsetHeight * 0.7 : window.scrollY > 400;
      const nearWaitlist = waitlist ? window.scrollY + window.innerHeight > waitlist.offsetTop - 80 : false;

      if (pastHero && !nearWaitlist) {
        if (!shown) { shown = true; cta.classList.add('active'); }
      } else {
        if (shown) { shown = false; cta.classList.remove('active'); }
      }
    }

    window.addEventListener('scroll', onScroll, { passive: true });
    onScroll();
  }

  /* ── Mobile nav ── */
  function initMobileNav() {
    const menuBtn = document.getElementById('headerMenuBtn');
    const nav = document.getElementById('mobileNav');
    if (!menuBtn || !nav) return;

    function openNav() {
      nav.classList.add('open');
      nav.setAttribute('aria-hidden', 'false');
      menuBtn.setAttribute('aria-expanded', 'true');
      menuBtn.classList.add('open');
      document.body.style.overflow = 'hidden';
      if (window.lenis) window.lenis.stop();
    }

    function closeNav() {
      nav.classList.remove('open');
      nav.setAttribute('aria-hidden', 'true');
      menuBtn.setAttribute('aria-expanded', 'false');
      menuBtn.classList.remove('open');
      document.body.style.overflow = '';
      if (window.lenis) window.lenis.start();
    }

    menuBtn.addEventListener('click', () => {
      nav.classList.contains('open') ? closeNav() : openNav();
    });

    nav.querySelectorAll('[data-scroll-to]').forEach((btn) => {
      btn.addEventListener('click', () => {
        closeNav();
        const target = document.querySelector(btn.dataset.scrollTo);
        if (target) setTimeout(() => scrollTo(target), 120);
      });
    });

    nav.querySelectorAll('a').forEach((link) => {
      link.addEventListener('click', closeNav);
    });
  }

  function initGenericScrollButtons() {
    document.querySelectorAll('[data-scroll-to]').forEach((btn) => {
      if (btn.dataset.arameScrollBound === 'true') return;
      if (btn.closest('.site-header') || btn.closest('.mobile-nav-overlay') || btn.closest('#floatingCta')) return;

      btn.dataset.arameScrollBound = 'true';
      btn.addEventListener('click', (event) => {
        const selector = btn.dataset.scrollTo;
        if (!selector) return;

        const target = document.querySelector(selector);
        if (target) {
          event.preventDefault();
          scrollTo(target);
          return;
        }

        if (selector.charAt(0) === '#') {
          window.location.href = '/' + selector;
        }
      });
    });
  }

  function initPartnershipPage() {
    const page = document.querySelector('.partnership-page');
    if (!page) return;

    const revealItems = Array.from(page.querySelectorAll(
      '.partnership-hero-copy, .partnership-hero-visual, .partnership-intro-copy, .partnership-intro-visual, .partnership-community-section, .partnership-section-heading, .partnership-card, .partnership-feature-section, .partnership-why, .partnership-faq-list details, .partnership-contact, .partnership-form-shell'
    ));

    revealItems.forEach((item, index) => {
      gsap.fromTo(item,
        { opacity: 0, y: 26 },
        {
          opacity: 1,
          y: 0,
          duration: 0.85,
          delay: Math.min(index * 0.035, 0.18),
          ease: 'power3.out',
          scrollTrigger: { trigger: item, start: 'top 88%', once: true },
        }
      );
    });

    initPartnershipForm();
  }

  function initPartnershipForm() {
    const shell = document.getElementById('partnership-form');
    if (!shell) return;

    const shadowCss = `
      :host {
        --forms-base-font-size: 15px;
        --forms-base-font-line-height: 1.55;
        --forms-body-font: 300 15px/1.55 "Jost", Arial, sans-serif;
        --forms-heading-font: 300 28px/1 "Cormorant Garamond", Georgia, serif;
        --forms-accent-color: #d8b96f;
        --body-text-color: rgba(246, 239, 230, 0.76);
        --heading-text-color: #f6efe6;
        --form-background-color: rgba(0, 0, 0, 0.18);
        --form-text-color: #f6efe6;
        --form-field-border: 0 0 0 1px rgba(216, 185, 111, 0.18);
        --form-field-border-focused: 0 0 0 1px rgba(216, 185, 111, 0.72), 0 0 0 4px rgba(216, 185, 111, 0.13);
        --form-field-border-radius: 5px;
        --form-field-padding: 14px;
        --form-error-color: #d9aaa0;
        --button-background-color: #d8b96f;
        --button-text-color: #241914;
        --button-border: 1px solid rgba(216, 185, 111, 0.7);
        --button-border-radius: 2px;
        --button-padding: 15px 24px;
        --button-background-color-hovered-modifier: brightness(1.04);
        --disclaimer-padding: 0;
      }

      * { box-sizing: border-box; }

      #app-embed,
      #form-container-ref,
      [data-testid="app-router"],
      section._inline_1q1d2_47,
      section._formContainer_1q1d2_30,
      ._formContainer_1q1d2_30,
      ._grid_1q1d2_148,
      ._gridItemContent_1q1d2_257 {
        width: 100% !important;
        max-width: none !important;
        min-width: 0 !important;
        background: transparent !important;
        border: 0 !important;
        box-shadow: none !important;
        color: #f6efe6 !important;
      }

      ._grid_1q1d2_148 {
        display: block !important;
      }

      ._gridItem_1q1d2_172:first-child,
      section._formHeader_cit2d_24 {
        display: none !important;
      }

      form._formFieldset_cit2d_82 {
        display: grid !important;
        grid-template-columns: repeat(2, minmax(0, 1fr)) !important;
        gap: 20px !important;
        padding: 0 !important;
      }

      form._formFieldset_cit2d_82 > * {
        min-width: 0 !important;
      }

      form._formFieldset_cit2d_82 > div:first-child:empty,
      form._formFieldset_cit2d_82 > section:empty {
        display: none !important;
      }

      ._formFieldContainer_1mxsl_5,
      ._selectContainer_12thd_6 {
        width: 100% !important;
        min-width: 0 !important;
        background: transparent !important;
      }

      ._formFieldContainer_1mxsl_5:has(textarea),
      ._checkboxFieldSet_1hja3_3,
      ._radioFieldSet_nw6va_1,
      ._formDisclaimer_cit2d_38,
      button._formSubmitButton_cit2d_96 {
        grid-column: 1 / -1 !important;
      }

      input._formInputField_nag3b_7,
      ._textArea_17mgw_1,
      button._selectToggle_12thd_25 {
        width: 100% !important;
        min-height: 54px !important;
        background-color: rgba(0, 0, 0, 0.18) !important;
        color: #f6efe6 !important;
        box-shadow: 0 0 0 1px rgba(216, 185, 111, 0.18) !important;
        border: 0 !important;
        border-radius: 5px !important;
        outline: none !important;
        transition: box-shadow 240ms ease, background-color 240ms ease !important;
      }

      input._formInputField_nag3b_7:focus-visible,
      ._textArea_17mgw_1:focus-visible,
      button._selectToggle_12thd_25:focus-visible {
        box-shadow: 0 0 0 1px rgba(216, 185, 111, 0.72), 0 0 0 4px rgba(216, 185, 111, 0.13) !important;
      }

      ._textArea_17mgw_1 {
        min-height: 150px !important;
        padding: 20px 16px 14px !important;
        resize: vertical !important;
      }

      ._formInputFieldLabel_1mxsl_38,
      ._label_12thd_10,
      ._checkboxLegend_1hja3_20,
      ._radioLegend_nw6va_18,
      div._birthdayText_rzr7e_15 {
        font: 400 10px/1.45 "Jost", Arial, sans-serif !important;
        letter-spacing: 0.17em !important;
        text-transform: uppercase !important;
        color: rgba(246, 239, 230, 0.72) !important;
      }

      ._formFieldContainer_1mxsl_5:focus-within ._formInputFieldLabel_1mxsl_38,
      ._formFieldContainer_1mxsl_5 ._inputFilled_1mxsl_74 {
        color: rgba(216, 185, 111, 0.9) !important;
      }

      ._selectToggleText_12thd_25,
      ._textBody_2aowh_10,
      span._textBody_2aowh_10,
      p._textBody_2aowh_10 {
        color: rgba(246, 239, 230, 0.78) !important;
        font-family: "Jost", Arial, sans-serif !important;
        font-weight: 300 !important;
        line-height: 1.55 !important;
      }

      ._checkboxFieldSet_1hja3_3,
      ._radioFieldSet_nw6va_1 {
        padding: 18px !important;
        border: 1px solid rgba(216, 185, 111, 0.16) !important;
        border-radius: 5px !important;
        background: rgba(246, 239, 230, 0.045) !important;
      }

      ._verticalLayout_1hja3_8,
      ._horizontalLayout_1hja3_14,
      ._verticalLayout_nw6va_6,
      ._horizontalLayout_nw6va_12 {
        display: grid !important;
        grid-template-columns: repeat(3, minmax(0, 1fr)) !important;
        gap: 10px 14px !important;
      }

      ._checkboxField_1hja3_3,
      ._radioField_nw6va_1 {
        align-items: flex-start !important;
        color: rgba(246, 239, 230, 0.78) !important;
        font: 300 14px/1.45 "Jost", Arial, sans-serif !important;
        letter-spacing: 0.02em !important;
      }

      ._checkboxInput_1hja3_38,
      ._radioInput_nw6va_31 {
        width: 16px !important;
        height: 16px !important;
        margin-top: 2px !important;
        accent-color: #d8b96f !important;
      }

      ._dropdownContainer_12thd_63,
      ._optionList_12thd_86 {
        background: #241914 !important;
        border: 1px solid rgba(216, 185, 111, 0.2) !important;
        border-radius: 5px !important;
        color: #f6efe6 !important;
      }

      ._optionListItem_12thd_102 {
        color: #f6efe6 !important;
      }

      ._optionListItem_12thd_102:hover,
      ._optionListItemSelected_12thd_112 {
        background: rgba(216, 185, 111, 0.13) !important;
      }

      output[role="alert"],
      ._formError_1mxsl_21,
      ._withError_rzr7e_21 {
        color: #d9aaa0 !important;
        font: 300 12px/1.5 "Jost", Arial, sans-serif !important;
        letter-spacing: 0.02em !important;
      }

      button._formSubmitButton_cit2d_96 {
        justify-self: start !important;
        min-width: min(100%, 320px) !important;
        min-height: 48px !important;
        margin-top: 8px !important;
        border-radius: 2px !important;
        font: 400 10px/1 "Jost", Arial, sans-serif !important;
        letter-spacing: 0.22em !important;
        text-transform: uppercase !important;
        box-shadow: 0 18px 42px rgba(0, 0, 0, 0.18) !important;
      }

      ._formBannerContainer_z5ku3_1 {
        border: 1px solid rgba(216, 185, 111, 0.24) !important;
        border-radius: 5px !important;
        background: rgba(246, 239, 230, 0.055) !important;
        color: #f6efe6 !important;
      }

      ._formBannerHeading_z5ku3_9 {
        color: #f6efe6 !important;
        font-family: "Cormorant Garamond", Georgia, serif !important;
        font-weight: 300 !important;
      }

      @media (max-width: 680px) {
        form._formFieldset_cit2d_82,
        ._verticalLayout_1hja3_8,
        ._horizontalLayout_1hja3_14,
        ._verticalLayout_nw6va_6,
        ._horizontalLayout_nw6va_12 {
          grid-template-columns: 1fr !important;
        }

        ._formFieldContainer_1mxsl_5,
        ._selectContainer_12thd_6,
        ._checkboxFieldSet_1hja3_3,
        ._radioFieldSet_nw6va_1,
        button._formSubmitButton_cit2d_96 {
          grid-column: 1 / -1 !important;
        }

        button._formSubmitButton_cit2d_96 {
          width: 100% !important;
        }
      }
    `;

    const injectStyles = (embed) => {
      if (!embed || !embed.shadowRoot) return false;
      if (embed.shadowRoot.getElementById('arame-partnership-forms-style')) return true;
      const style = document.createElement('style');
      style.id = 'arame-partnership-forms-style';
      style.textContent = shadowCss;
      embed.shadowRoot.appendChild(style);
      return true;
    };

    const scan = () => {
      shell.querySelectorAll('shopify-forms-embed').forEach(injectStyles);
    };

    scan();

    if (shell.dataset.formsStyleObserver !== 'true' && 'MutationObserver' in window) {
      shell.dataset.formsStyleObserver = 'true';
      const observer = new MutationObserver(scan);
      observer.observe(shell, { childList: true, subtree: true });
    }

    if (shell.dataset.formsStyleTimer !== 'true') {
      shell.dataset.formsStyleTimer = 'true';
      let attempts = 0;
      const timer = setInterval(() => {
        attempts += 1;
        scan();
        if (attempts > 40 || shell.querySelector('shopify-forms-embed')) clearInterval(timer);
      }, 250);
    }
  }

  /* ── Mobile animation safety net ──
     IntersectionObserver watches elements GSAP has set to opacity:0.
     If ScrollTrigger missed them (iOS momentum / touch throttling),
     this forces them visible so content is never stuck hidden.       */
  function initAnimSafetyNet() {
    if (!('IntersectionObserver' in window)) return;

    const selectors = [
      '.sock-label', '.sock-heading', '.sock-divider', '.sock-body', '.sock-chips',
      '.sock-image-wrap img', '.sock-accent-line',
      '.material-heading', '.material-subheading', '.material-image-wrap',
      '.material-detail-row',
      '.features-line', '.features-eyebrow', '.features-heading', '.features-subheading',
      '.feature-item',
      '.brand-story-label', '.brand-story-heading', '.brand-story-body',
      '.colorways-header', '.product-card',
      '.movement-heading', '.movement-body', '.movement-main-panel', '.movement-right-col',
      '.partnership-page .partnership-card', '.partnership-page .partnership-form-shell',
    ].join(',');

    const candidates = Array.from(document.querySelectorAll(selectors));
    if (!candidates.length) return;

    const obs = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (!entry.isIntersecting) return;
        const el = entry.target;
        // Only intervene if GSAP has hidden it (inline opacity:0) and it's been > 1.5s
        const computed = window.getComputedStyle(el).opacity;
        if (parseFloat(computed) < 0.05) {
          gsap.to(el, { opacity: 1, y: 0, duration: 0.5, ease: 'power2.out', overwrite: 'auto' });
        }
        obs.unobserve(el);
      });
    }, { threshold: 0.08, rootMargin: '0px 0px -40px 0px' });

    // Give GSAP 1.5s to register animations before the safety net watches anything
    setTimeout(() => candidates.forEach((el) => obs.observe(el)), 1500);
  }

  /* ── Waitlist form ── */
  // The form uses {% form 'customer' %} (Shopify Liquid) for proper customer creation.
  // Native submission is used — Shopify validates, creates the customer, then redirects
  // back to this page. The Liquid template renders the success state after redirect.
  // This JS only adds a loading state and basic client-side validation for UX.
  ready(function () {
    const form = document.getElementById('arame-waitlist-form');
    if (!form) return;

    form.addEventListener('submit', function (e) {
      const emailInput = form.querySelector('[name="contact[email]"]');
      const email = emailInput ? emailInput.value.trim() : '';

      if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
        e.preventDefault();
        let err = form.querySelector('.waitlist-error');
        if (!err) {
          err = document.createElement('p');
          err.className = 'waitlist-error';
          err.setAttribute('role', 'alert');
          const row = form.querySelector('.waitlist-form-row');
          if (row) row.after(err);
          else form.appendChild(err);
        }
        err.textContent = 'Please enter a valid email address.';
        return;
      }

      // Let the native Shopify form submit — just add a loading indicator
      const btn = form.querySelector('.waitlist-submit');
      if (btn) {
        btn.textContent = '...';
        btn.disabled = true;
      }
    });
  });

})();
